import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";

import useEventRouting from "hooks/useEventRouting";
import { Webpage, getWebpages } from "lib/webpagesApi";
import ROUTES from "routes";
import { Button, FontAwesomeIcon } from "..";
import { pushToDataLayer } from "utils/gtm";
import { useGlobal } from "state/GeneralContext";
import useDetectClickOutside from "utils/useDetectClickOutside";

export interface WebpagesNavProps {
  isLoggedIn: boolean;
  handleMobileMenu?: (isOpen: boolean) => void;
}

const WebpagesNav = ({
  isLoggedIn,
  handleMobileMenu = () => {},
}: WebpagesNavProps) => {
  const { t } = useTranslation();
  const { getEventRoute, eventId } = useEventRouting();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const linksContainerRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(
    ["webpages", eventId, isLoggedIn],
    async () => {
      const response = await getWebpages(eventId);
      const published =
        (response?.data?.webpages as unknown as Webpage[])?.filter(
          (webpage) => webpage.published,
        ) || [];

      return {
        publishedWebpages: published,
      };
    },
    {
      enabled: Boolean(eventId),
    },
  );

  const { publishedWebpages = [] } = data || {};

  // Measure link widths after render and determine how many fit within MAX_LINKS_WIDTH
  const calculateVisibleLinks = useCallback(() => {
    const container = linksContainerRef.current;
    if (!container || publishedWebpages.length === 0) return;

    const linkElements = container.querySelectorAll<HTMLElement>(
      "[data-webpage-link]",
    );
    if (linkElements.length === 0) return;

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < linkElements.length; i++) {
      const link = linkElements[i];
      totalWidth += link.offsetWidth;
      if (totalWidth < container.offsetWidth - 200) {
        count++;
      }
    }
    const visibleLinksWidth = [...container.children].reduce(
      (acc, child, index) => (index !== 0 ? acc + child.clientWidth : acc),
      80,
    );
    // reduce the count of visible links if the container width is less than MAX_LINKS_WIDTH
    if (visibleLinksWidth > container.offsetWidth) {
      count = Math.min(count, visibleCount) - 1;
    }
    // Show at least 1 link if there are any
    setVisibleCount((prevCount) =>
      container.offsetWidth < 550
        ? Math.max(Math.min(count, prevCount), 1)
        : Math.max(count, 1),
    );
  }, [publishedWebpages, setVisibleCount]);

  useLayoutEffect(() => {
    calculateVisibleLinks();
  }, [calculateVisibleLinks]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => calculateVisibleLinks();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateVisibleLinks]);

  const visibleWebpages =
    visibleCount !== 0
      ? publishedWebpages.slice(0, visibleCount)
      : publishedWebpages;
  const moreWebpages =
    visibleCount !== 0 ? publishedWebpages.slice(visibleCount) : [];
  const hasMore = moreWebpages.length > 0;

  const ref = useRef<HTMLDivElement>(null);

  useDetectClickOutside(ref, () => setIsMoreOpen(false));

  return (
    <div
      className="hidden lg:flex items-center justify-center flex-1 lg:gap-x-1.5 "
      ref={linksContainerRef}
    >
      {/* Hidden measurement container - renders all links to measure widths */}
      <div
        className="flex items-center gap-x-1.5 absolute invisible pointer-events-none"
        aria-hidden="true"
      >
        {publishedWebpages.map((webpage) => (
          <span
            key={webpage.id}
            data-webpage-link
            className="text-sm font-medium whitespace-nowrap px-3 py-2"
          >
            {webpage.title}
          </span>
        ))}
      </div>

      {isLoggedIn && visibleWebpages.length > 0 && (
        <NavLink
          to={getEventRoute(ROUTES.dashboard.base)}
          className={({ isActive }) =>
            classNames(
              "text-sm font-medium hover:opacity-80 transition-opacity -text--theme-colors-text-title",
              {
                "font-bold": isActive,
              },
            )
          }
        >
          {t("common.navigation.dashboard")}
        </NavLink>
      )}

      {visibleWebpages.map((webpage) =>
        webpage.page_type === "hyperlink" ? (
          <a
            key={webpage.id}
            href={webpage.url}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(
              "text-sm -text--theme-colors-text-title px-3 py-2 hover:bg-white/5",
            )}
            onClick={() => handleMobileMenu(false)}
          >
            {webpage.title}
          </a>
        ) : (
          <NavLink
            key={webpage.id}
            to={getEventRoute(ROUTES.webpages.base(webpage.slug))}
            className={({ isActive }) =>
              classNames(
                "text-sm font-medium whitespace-nowrap px-3 py-2 -text--theme-colors-text-title hover:opacity-80 transition-opacity",
                {
                  "font-bold": isActive,
                },
              )
            }
          >
            {webpage.title}
          </NavLink>
        ),
      )}
      {hasMore && (
        <div className="relative" ref={ref}>
          <button
            onClick={() => setIsMoreOpen(!isMoreOpen)}
            className="flex items-center !px-3 !py-2 gap-x-2 text-sm font-medium -text--theme-colors-text-title hover:opacity-80 transition-opacity"
          >
            {t("common.navigation.more")}
            <FontAwesomeIcon
              name={isMoreOpen ? "fa-chevron-up" : "fa-chevron-down"}
              size="sm"
            />
          </button>
          {isMoreOpen && (
            <div className="absolute flex flex-col top-full left-0 mt-2 py-2 w-48 shadow-2xl rounded-lg z-50 -bg--theme-colors-surface-neutral-level04 border border-white/10 backdrop-blur-md">
              {moreWebpages.map((webpage) =>
                webpage.page_type === "hyperlink" ? (
                  <a
                    key={webpage.id}
                    href={webpage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames(
                      "text-sm -text--theme-colors-text-title px-6 py-3 hover:bg-white/5",
                    )}
                    onClick={() => {
                      setIsMoreOpen(false);
                      handleMobileMenu(false);
                    }}
                  >
                    {webpage.title}
                  </a>
                ) : (
                  <NavLink
                    key={webpage.id}
                    to={getEventRoute(ROUTES.webpages.base(webpage.slug))}
                    className="block px-4 py-2 text-sm font-medium -text--theme-colors-text-title hover:bg-white/5 transition-colors"
                    onClick={() => {
                      setIsMoreOpen(false);
                      handleMobileMenu(false);
                    }}
                  >
                    {webpage.title}
                  </NavLink>
                ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type MobileProps = {
  isLoggedIn: boolean;
  handleMobileMenu: (open: boolean) => void;
  handleLogout: () => void;
};
const Mobile = ({ handleMobileMenu, handleLogout }: MobileProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isLoggedIn, events } = useGlobal();
  const navigate = useNavigate();
  const { getEventRoute, eventId, isCurrentPage } = useEventRouting();

  const currentEvent = events.find((e) => String(e.id) === String(eventId));
  const eventName = currentEvent?.title || "";
  const showEventSwitcher =
    events.length > 1 && isLoggedIn && !isCurrentPage(ROUTES.eventSwitcher);
  const data = queryClient.getQueryData(["webpages", eventId, isLoggedIn]) as
    | {
        publishedWebpages: Webpage[];
      }
    | undefined;
  const { publishedWebpages = [] } = data || {};

  return (
    <div className="fixed h-screen -top-4 md:-inset-x-20 -inset-x-4 z-[70] overflow-y-auto -bg--theme-colors-surface-neutral-level00 lg:hidden">
      <div className="flex justify-end">
        <Button
          onClick={() => handleMobileMenu(false)}
          variant="tertiary"
          size="md"
          className="min-h-[auto] !p-2 m-4"
          childClassName="size-s20 flex items-center justify-center "
        >
          <FontAwesomeIcon name="fa-times" size="lg" />
        </Button>
      </div>
      <div className="flex flex-col pt-4 pb-8 px-4">
        {showEventSwitcher && (
          <div className="mb-8 flex flex-col items-center">
            <Button
              variant="tertiary"
              fullWidth
              className=" !rounded-none py-3"
              onClick={() => {
                navigate(ROUTES.eventSwitcher, { replace: true });
                handleMobileMenu(false);
              }}
              childClassName="flex items-center gap-x-2 w-full"
            >
              <img
                src={currentEvent?.cover_image}
                alt={eventName}
                className="size-8"
              />
              <span className="flex-1 text-start truncate font-bold">
                {eventName || "Event"}
              </span>
              <FontAwesomeIcon name="fa-rotate-reverse" />
            </Button>
          </div>
        )}
        {/* Auth Section */}
        <div className=" mb-4">
          {isLoggedIn ? (
            <button
              onClick={() => {
                pushToDataLayer({
                  event: "logout",
                  screen_name: "header",
                  journey_step: "logout",
                });
                handleLogout();
                handleMobileMenu(false);
              }}
              className="flex items-center gap-x-3 text-base font-medium -text--theme-colors-text-title hover:opacity-80 transition-opacity py-3"
            >
              <FontAwesomeIcon
                name="fa-sign-out-alt"
                className="ltr:-scale-x-100"
              />
              <span>{t("header.signOutLabel")}</span>
            </button>
          ) : (
            <div onClick={() => handleMobileMenu(false)}>
              <Button
                to={getEventRoute(ROUTES.login)}
                as="link"
                variant="tertiary"
                size="md"
              >
                <span className="text-sm me-2">{t("header.signInLabel")}</span>
                <FontAwesomeIcon name="fa-arrow-right" />
              </Button>
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="h-px w-full -bg--theme-colors-border-neutral-10 mb-6" />

        {/* Links Section */}
        <div className="flex flex-col gap-y-2">
          {isLoggedIn && publishedWebpages.length > 0 && (
            <NavLink
              to={getEventRoute(ROUTES.dashboard.base)}
              className={({ isActive }) =>
                classNames(
                  "text-sm font-medium -text--theme-colors-text-title px-2 py-3 hover:bg-white/5 transition-colors",
                  { "font-semibold": isActive },
                )
              }
              onClick={() => handleMobileMenu(false)}
            >
              {t("common.navigation.dashboard")}
            </NavLink>
          )}

          {publishedWebpages.map((webpage) =>
            webpage.page_type === "hyperlink" ? (
              <a
                key={webpage.id}
                href={webpage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium -text--theme-colors-text-title px-2 py-3 hover:bg-white/5 transition-colors"
                onClick={() => handleMobileMenu(false)}
              >
                {webpage.title}
              </a>
            ) : (
              <NavLink
                key={webpage.id}
                to={getEventRoute(ROUTES.webpages.base(webpage.slug))}
                className={({ isActive }) =>
                  classNames(
                    "text-sm font-medium -text--theme-colors-text-title px-2 py-3 hover:bg-white/5 transition-colors",
                    { "font-semibold": isActive },
                  )
                }
                onClick={() => handleMobileMenu(false)}
              >
                {webpage.title}
              </NavLink>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

WebpagesNav.Mobile = Mobile;

export default WebpagesNav;
